import React, { useRef, useState } from 'react';

import styled from 'styled-components';

import TrackedAssetContainer from '~/shared/components/TrackedAssetContainer';
import parseCustomVarAttributes from '~/shared/utils/parseCustomVarAttributes';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import { Link } from '~/techstyle-shared/next-routes';
import { useBreakpoint } from '~/techstyle-shared/react-components';

import useFadeOutUnmount from '../../hooks/useFadeOutUnmount';
import {
  buildFallbackImageFromCDN,
  getProductImageSizeString,
} from '../../utils/images';

import AssetCardFallback from './AssetCardFallback';
import AssetCardSkeleton from './AssetCardSkeleton';

const TrackedLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
`;

const Image = styled.img<{ $isUncropped: boolean }>`
  display: block;
  width: 100%;
  ${({ $isUncropped }) =>
    $isUncropped
      ? `
        height: auto;
      `
      : `
        height: 100%;
        object-fit: cover;
      `}
`;

const ImageWrapper = styled.div<{ $isUncropped: boolean }>`
  position: relative;
  width: 100%;
  ${({ $isUncropped }) => ($isUncropped ? '' : 'height: 100%;')}
`;

const SkeletonOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  transition: opacity 300ms ease;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: none;
`;

interface AssetCardImageProps {
  imageFilename: string;
  label?: string;
  options?: {
    customVars?: {
      // eslint-disable-next-line camelcase
      clickable_asset_extra_properties?: string;
    };
    targetUrl?: string;
  };
  className?: string;
  imageTrackingData?: Record<string, unknown>;
  trackAssetClick?: () => void;
}

const AssetCardImage = ({
  imageFilename,
  label,
  options,
  className,
  imageTrackingData,
  trackAssetClick,
}: AssetCardImageProps) => {
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const hasFallenBack = useRef(false);
  const { isMounted: showSkeleton, onTransitionEnd: onSkeletonTransitionEnd } =
    useFadeOutUnmount(isLoading);

  const { enabled: isAssetNoCropEnabled } = useHasFeature(
    featureNames.GRID_V2_ASSET_NO_CROP
  );

  const extraProps = parseCustomVarAttributes(
    options?.customVars?.clickable_asset_extra_properties
  );

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (hasFallenBack.current) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    hasFallenBack.current = true;
    const target = e.target as HTMLImageElement;
    const fallbackUrl = buildFallbackImageFromCDN({
      requestedSize: getProductImageSizeString(isMobile),
    });

    target.src = fallbackUrl;
    setIsLoading(false);
  };

  const imageElement = (
    <Image
      $isUncropped={isAssetNoCropEnabled}
      src={imageFilename}
      alt={label}
      className={className}
      onLoad={handleLoad}
      onError={handleError}
      {...(extraProps || {})}
    />
  );

  if (hasError) {
    return <AssetCardFallback />;
  }

  return (
    <TrackedAssetContainer asset={imageTrackingData}>
      <ImageWrapper $isUncropped={isAssetNoCropEnabled}>
        {showSkeleton && (
          <SkeletonOverlay
            $isVisible={isLoading}
            onTransitionEnd={onSkeletonTransitionEnd}
          >
            <AssetCardSkeleton />
          </SkeletonOverlay>
        )}
        {options?.targetUrl ? (
          // @ts-expect-error Link props (as, route, to) are optional at runtime but typed as required from PropTypes
          <Link href={options.targetUrl}>
            <TrackedLink onClick={trackAssetClick}>{imageElement}</TrackedLink>
          </Link>
        ) : (
          imageElement
        )}
      </ImageWrapper>
    </TrackedAssetContainer>
  );
};

export default AssetCardImage;
